import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileDown } from 'lucide-react';
import toast from 'react-hot-toast';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import EditableTitle from '@/components/ui/EditableTitle';
import { confirmDialog } from '@/lib/confirmDialog';
import type { BasesCalculo, Costo, CostoFormData, ParteItem } from '@/types/costo';
import {
  CLASE_ESTADO,
  ETIQUETA_ESTADO,
  PARTES,
} from '../constants/presupuestos';
import { PRESUPUESTOS_ROUTES } from '../constants/routes';
import type { AccionEstadoPresupuesto } from '../types';
import { useDestinosPagoOpciones } from '../hooks/useDestinosPago';
import { usePresupuestoProforma } from '../hooks/usePresupuestoProforma';
import { usePresupuestosProformaPermissions } from '../hooks/usePresupuestosProformaPermissions';
import { confirmarItemsSinDestino, contarSinDestino } from '../utils/destinoPago';
import { BotonDesasociarPresupuesto } from './BotonDesasociarPresupuesto';
import { BotonExportar } from './BotonExportar';
import { CajaItems } from './CajaItems';
import { DatosPartesForm } from './DatosPartesForm';
import { EnlaceEscritura } from './EnlaceEscritura';
import { LeyendasPresupuestoCard } from './LeyendasPresupuestoCard';
import { ModalCalculoItem } from './ModalCalculoItem';
import { ModalImportarPlantilla } from './ModalImportarPlantilla';

export const PresupuestoDetallePage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const permisos = usePresupuestosProformaPermissions();
  const {
    presupuesto,
    isLoading,
    actualizarPresupuesto,
    crearItem,
    eliminarItem,
    actualizarItem,
    actualizarCalculoItem,
    importarPlantilla,
    reordenarItems,
    cambiarEstado,
    isActualizando,
    isCreandoItem,
    isEliminandoItem,
    isActualizandoCalculo,
    isImportandoPlantilla,
    isCambiandoEstado,
    autoguardado,
  } = usePresupuestoProforma(id);
  const { opciones: destinosPago } = useDestinosPagoOpciones();
  const [modalPlantilla, setModalPlantilla] = useState(false);
  const [itemCalculo, setItemCalculo] = useState<Costo | null>(null);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!presupuesto) {
    return (
      <div className="py-16 text-center text-muted-foreground">
        No se encontró el presupuesto solicitado.
      </div>
    );
  }

  const esArchivado = presupuesto.estado === 'archivado';
  const puedeEditar = permisos.canEdit && !esArchivado;
  const puedeImportarPlantilla = puedeEditar && !presupuesto.fueImportado;
  const tieneItems = PARTES.some((parte) => (presupuesto.items[parte] ?? []).length > 0);

  const handleRenombrar = async (nombre: string) => {
    if (!nombre) return;

    try {
      await actualizarPresupuesto({ nombre });
    } catch {
      toast.error('No se pudo actualizar el nombre del presupuesto.');
    }
  };

  const handleCambiarEstado = async (accion: AccionEstadoPresupuesto) => {
    try {
      await cambiarEstado(accion);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ?? 'No se pudo cambiar el estado del presupuesto.'
      );
    }
  };

  const handleDesarchivar = async () => {
    const destino = presupuesto.estadoAlDesarchivar;
    const confirmado = await confirmDialog.open({
      title: 'Desarchivar presupuesto',
      text: destino
        ? `"${presupuesto.nombre}" va a volver al estado ${ETIQUETA_ESTADO[destino]}.`
        : `"${presupuesto.nombre}" va a dejar de estar archivado.`,
      confirmButtonText: 'Desarchivar',
    });

    if (confirmado) await handleCambiarEstado('desarchivar');
  };

  const bases: BasesCalculo = {
    base_imponible: { importe: presupuesto.baseImponible, moneda: presupuesto.baseImponibleMoneda },
    monto_venta: { importe: presupuesto.montoVenta, moneda: presupuesto.montoVentaMoneda },
  };

  const itemsDeParte = (parte: ParteItem): Costo[] =>
    (presupuesto.items[parte] ?? []) as unknown as Costo[];

  return (
    <div className="space-y-6 p-2 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to={PRESUPUESTOS_ROUTES.LISTADO} aria-label="Volver al listado">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <EditableTitle
            title={presupuesto.nombre}
            onSave={handleRenombrar}
            readOnly={!puedeEditar}
            className="text-2xl font-bold"
          />
          <Badge className={CLASE_ESTADO[presupuesto.estado]} variant="secondary">
            {ETIQUETA_ESTADO[presupuesto.estado]}
          </Badge>
          {presupuesto.importadoEn.length > 0 && (
            <span className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              Importado en
              {presupuesto.importadoEn.map((escritura) => (
                <span key={escritura.tramiteId} className="flex items-center gap-1">
                  <EnlaceEscritura escritura={escritura} />
                  {permisos.canImport && (
                    <BotonDesasociarPresupuesto
                      tramiteId={escritura.tramiteId}
                      nombrePresupuesto={presupuesto.nombre}
                      escritura={`#${escritura.tramiteId}`}
                      variante="icono"
                    />
                  )}
                </span>
              ))}
            </span>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {puedeImportarPlantilla && (
            <Button
              variant="outline"
              onClick={() => setModalPlantilla(true)}
              disabled={isImportandoPlantilla}
            >
              <FileDown className="mr-2 h-4 w-4" />
              Importar plantilla
            </Button>
          )}
          {permisos.canConfirm && presupuesto.estado === 'borrador' && (
            <Button
              variant="outline"
              onClick={() => void handleCambiarEstado('confirmar')}
              disabled={isCambiandoEstado}
            >
              Confirmar
            </Button>
          )}
          {permisos.canConfirm && presupuesto.estado === 'confirmado' && (
            <Button
              variant="outline"
              onClick={() => void handleCambiarEstado('borrador')}
              disabled={isCambiandoEstado}
            >
              Volver a borrador
            </Button>
          )}
          {permisos.canArchive && esArchivado && (
            <Button
              variant="outline"
              onClick={() => void handleDesarchivar()}
              disabled={isCambiandoEstado}
            >
              Desarchivar
            </Button>
          )}
          {permisos.canArchive && !esArchivado && (
            <Button
              variant="outline"
              onClick={() => void handleCambiarEstado('archivar')}
              disabled={isCambiandoEstado}
            >
              Archivar
            </Button>
          )}
          {permisos.canExport && (
            <BotonExportar
              presupuestoId={presupuesto.id}
              nombrePresupuesto={presupuesto.nombre}
              onAntesDeExportar={autoguardado.guardarAhora}
              confirmarExportacion={(partes) =>
                confirmarItemsSinDestino(
                  contarSinDestino(PARTES.flatMap((parte) => presupuesto.items[parte] ?? []), partes)
                )
              }
            />
          )}
        </div>
      </div>

      <DatosPartesForm
        presupuesto={presupuesto}
        onGuardar={async (data) => {
          try {
            await actualizarPresupuesto(data);
            toast.success('Datos actualizados.');
          } catch (error: any) {
            toast.error(error?.response?.data?.message ?? 'No se pudieron guardar los datos.');
          }
        }}
        readOnly={!puedeEditar}
        isGuardando={isActualizando}
      />

      <div className="grid gap-6 lg:grid-cols-2">
        {PARTES.map((parte) => (
          <CajaItems
            key={parte}
            parte={parte}
            items={itemsDeParte(parte)}
            subtotales={presupuesto.totales[parte]}
            estadoGuardado={autoguardado.estadoGlobal}
            onReintentar={() => void autoguardado.reintentar()}
            onCrearItem={(data: CostoFormData) =>
              crearItem({ ...data, parte, aclaracion: data.aclaracion ?? null })
            }
            onActualizarItem={actualizarItem}
            onEliminarItem={(itemId) => void eliminarItem(itemId)}
            onReordenar={puedeEditar ? (ids) => reordenarItems(parte, ids) : undefined}
            isLoading={isCreandoItem || isEliminandoItem}
            canCreate={puedeEditar}
            canEdit={puedeEditar}
            canDelete={puedeEditar}
            permitirPorcentaje
            bases={bases}
            cotizacionDolar={presupuesto.cotizacionDolar}
            destinosPago={destinosPago}
            onEditarCalculo={setItemCalculo}
          />
        ))}
      </div>

      <LeyendasPresupuestoCard
        notas={presupuesto.notas ?? []}
        onGuardar={async (notas) => {
          try {
            await actualizarPresupuesto({ notas });
            toast.success('Leyendas actualizadas.');
          } catch (error: any) {
            toast.error(error?.response?.data?.message ?? 'No se pudieron guardar las leyendas.');
          }
        }}
        readOnly={!puedeEditar}
        isGuardando={isActualizando}
      />

      <ModalImportarPlantilla
        isOpen={modalPlantilla}
        onClose={() => setModalPlantilla(false)}
        tieneItems={tieneItems}
        isImportando={isImportandoPlantilla}
        onImportar={async (plantillaId, modo) => {
          const resultado = await importarPlantilla({ plantillaId, modo });
          toast.success(`Se importaron ${resultado.importados} ítems.`);
        }}
      />

      <ModalCalculoItem
        item={itemCalculo}
        bases={bases}
        cotizacionDolar={presupuesto.cotizacionDolar}
        onClose={() => setItemCalculo(null)}
        isGuardando={isActualizandoCalculo}
        onGuardar={(itemId, data) => actualizarCalculoItem({ itemId, data })}
      />
    </div>
  );
};
